import { useState } from 'react'
import { stateReachableWhenScoped } from '@qim/shared'
import { fetchStateAccess } from '../api.js'
import type { JSX } from 'react'

/**
 * "State access", on one instance's screen (IM-214).
 *
 * A deploy role scoped to one deployment reads Terraform state only from the
 * region's own state bucket under the instance's id, or from
 * `qrveyversion-<id>`. An instance adopted with its state somewhere else is
 * refused at every run — so this offers the one grant the customer deploys to
 * let that instance's runs, and no other's, use the state where it already is.
 *
 * Whether to show it at all is decided from the record, at no cost: nothing is
 * shown for state the scoped role already reaches. The artifact is fetched
 * only when somebody downloads it.
 */

/** Saves an artifact as a file, the way the other templates and the bundle do. */
const save = (filename: string, document: unknown): void => {
  const blob = new Blob([JSON.stringify(document, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = window.document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export const StateAccessPanel = ({
  baseUrl,
  tenantId,
  accountKey,
  deploymentId,
  stateBucket,
  stateKey,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  readonly deploymentId: string
  readonly stateBucket: string | undefined
  readonly stateKey: string | undefined
}): JSX.Element | null => {
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | undefined>()

  if (stateBucket === undefined || stateKey === undefined) return null
  if (stateReachableWhenScoped(deploymentId, stateBucket, stateKey)) return null

  const onDownload = (): void => {
    setBusy(true)
    setMessage(undefined)
    fetchStateAccess(baseUrl, tenantId, accountKey, deploymentId)
      .then((answer) => {
        if (answer.artifact !== undefined) save(answer.artifact.filename, answer.artifact.document)
        else setMessage(answer.problem ?? 'No template is needed for this instance.')
      })
      .catch(() => {
        setMessage('Could not get the template just now.')
      })
      .finally(() => {
        setBusy(false)
      })
  }

  return (
    <section className="panel">
      <h2>State access</h2>
      <p>
        This instance keeps its Terraform state at{' '}
        <code>
          s3://{stateBucket}/{stateKey}
        </code>
        . A deploy role scoped to one deployment cannot read it there, so if this account&rsquo;s
        access stack uses <code>DeployPolicy=scoped</code>, every run of this instance is refused
        until the grant below is deployed. With <code>DeployPolicy=administrator</code> nothing is
        needed.
      </p>
      <p className="hint">
        The template adds one policy to the deploy role, allowing that bucket, that one file and its
        lock — and only for runs of this instance. The account owner deploys it once; deleting the
        stack takes the access away. A bucket encrypted with the account&rsquo;s own KMS key also
        needs the deploy role allowed in that key&rsquo;s policy, which this does not add.
      </p>
      <button type="button" onClick={onDownload} disabled={busy}>
        {busy ? 'Preparing…' : 'Download state access template'}
      </button>
      {message === undefined ? null : (
        <p className="failure" role="alert">
          {message}
        </p>
      )}
    </section>
  )
}
